/**
 * Admin dues breakdown helpers. node --test
 *
 * What an Anna owes is the server's `pendingDues`. Since 2026-10-14 each completed
 * ride also carries the exact fees it added (`ride.driverFees`, version 1:
 * rideFee + nightFee + cancellationFee = total), written in the same transaction
 * as the dues (firebase/functions/driverAccounting.js). A cancellation fee here is
 * a passenger's earlier cancellation fee that the Anna collected in cash on this
 * ride, so it is owed to the platform.
 *
 * Payments are not tied to rides, so this assumes they clear the oldest fees first:
 * the unpaid part is his most recent fees. Whatever pendingDues holds beyond his
 * recorded fees is "earlier balance" (owed from before per-ride fees were recorded,
 * or a manual balance change). Rides without driverFees are never re-priced here.
 */

const money = (value) => Math.round(Math.max(0, Number(value) || 0) * 100) / 100;

const millis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/** The fees a completed ride added to the Anna's dues, or null when the ride predates itemised fees. */
export const rideFees = (ride) => {
  const fees = ride?.driverFees;
  if (!fees || Number(fees.version) !== 1) return null;
  const rideFee = money(fees.rideFee);
  const nightFee = money(fees.nightFee);
  const cancellationFee = money(fees.cancellationFee);
  return { rideFee, nightFee, cancellationFee, total: money(fees.total ?? rideFee + nightFee + cancellationFee) };
};

/**
 * Splits one Anna's pending dues into ride / night / cancellation fees and earlier
 * balance, and lists his itemised rides newest first with how much of each is unpaid.
 * `rides` are that Anna's completed rides. Only rides whose driverId is this driver
 * document count: the server adds fees to drivers/{ride.driverId}, so a ride matched
 * by phone (an old or duplicate account) never added to this balance.
 */
export function buildDuesBreakdown(driver, rides = []) {
  const pending = money(driver?.pendingDues);
  const sorted = rides.filter((ride) => !driver?.id || ride?.driverId === driver.id)
    .sort((a, b) => millis(b.completedAt) - millis(a.completedAt));
  const charged = { rideFee: 0, nightFee: 0, cancellationFee: 0, total: 0 };
  const owed = { rideFee: 0, nightFee: 0, cancellationFee: 0, earlier: 0 };
  let left = pending;
  let notItemised = 0;
  const list = [];

  for (const ride of sorted) {
    const fees = rideFees(ride);
    if (!fees) { notItemised += 1; continue; }
    for (const key of ['rideFee', 'nightFee', 'cancellationFee', 'total']) {
      charged[key] = money(charged[key] + fees[key]);
    }
    const unpaid = money(Math.min(left, fees.total));
    left = money(left - unpaid);
    // A part-paid ride: the unpaid remainder is counted as ride fee first.
    let rest = unpaid;
    for (const key of ['rideFee', 'nightFee', 'cancellationFee']) {
      const part = money(Math.min(rest, fees[key]));
      owed[key] = money(owed[key] + part);
      rest = money(rest - part);
    }
    list.push({ ride, fees, unpaid });
  }
  owed.earlier = left;

  return { pending, charged, owed, rides: list, notItemised };
}

const GRACE_MS = 2 * 60 * 1000;
// The server times the 2-minute grace when its trigger runs, a few seconds after
// the cancel was written, so a cancel just under 2 minutes may have been charged.
const BORDERLINE_MS = 15 * 1000;

// Mirrors the server's late-cancel test (onRideUpdated + isRiderLateCancel):
// the passenger cancelled after an Anna accepted and before the trip started,
// and not within 2 minutes of the accept unless the Anna had already arrived.
const isLateCancel = (ride, borderlineIsLate) => {
  if (ride?.status !== 'cancelled' || !ride.driverId || !ride.acceptedAt || ride.startedAt) return false;
  if (['driver', 'admin', 'system'].includes(ride.cancelledBy) || ride.cancelledByAdmin === true) return false;
  const elapsed = millis(ride.cancelledAt || ride.updatedAt) - millis(ride.acceptedAt);
  if (!ride.arrivedAt && elapsed <= GRACE_MS - (borderlineIsLate ? BORDERLINE_MS : 0)) return false;
  return true;
};

function replayFeeSources(ride, allRides, borderlineIsLate) {
  const events = allRides
    .filter((r) => r.customerId && r.customerId === ride.customerId)
    .map((r) => (isLateCancel(r, borderlineIsLate) ? { kind: 'cancel', at: millis(r.cancelledAt || r.updatedAt), ride: r }
      : (r.status === 'completed' && Number(r.cancellationFee) > 0 ? { kind: 'collect', at: millis(r.createdAt), ride: r } : null)))
    .filter(Boolean)
    .sort((a, b) => a.at - b.at);
  let strikes = 0;
  let owedTotal = 0;
  let owed = [];
  for (const event of events) {
    if (event.kind === 'collect') {
      if (event.ride.id === ride.id) return owed;
      strikes = 0; owedTotal = 0; owed = [];
      continue;
    }
    strikes += 1;
    if (strikes === 1) { owedTotal = 0; owed = []; continue; }
    const amount = Math.min(20, owedTotal + 10) - owedTotal;
    owedTotal += amount;
    if (amount > 0) {
      const r = event.ride;
      owed.push({ rideId: r.id, amount, strike: strikes, driverName: r.driverName || null,
        acceptedAt: r.acceptedAt || null, cancelledAt: r.cancelledAt || r.updatedAt || null, cancelledRide: r });
    }
  }
  return [];
}

/**
 * Which late cancel(s) a ride's cancellation fee came from. Rides completed after
 * the server started recording it carry `cancellationFeeSources` (recorded: true).
 * Older ones are matched by replaying the passenger's history the way the server
 * charges: 1st late cancel is a free warning, then ₹10 each (₹20 cap), all cleared
 * when a ride collects them. Each item: { rideId, amount, strike, cancelledRide }.
 */
export function feeSourcesForRide(ride, allRides = []) {
  const fee = money(rideFees(ride)?.cancellationFee ?? ride?.cancellationFee);
  if (!(fee > 0)) return { recorded: false, items: [] };
  const byId = new Map(allRides.map((r) => [r.id, r]));
  if (Array.isArray(ride.cancellationFeeSources) && ride.cancellationFeeSources.length) {
    return {
      recorded: true,
      items: ride.cancellationFeeSources.map((s) => ({
        rideId: s.rideId, amount: money(s.amount), strike: Number(s.strike) || null,
        driverName: s.driverName || null, acceptedAt: s.acceptedAt || null, cancelledAt: s.cancelledAt || null,
        cancelledRide: byId.get(s.rideId) || null,
      })),
    };
  }
  // A cancel just under 2 minutes counts as late only when that is what makes
  // the history add up to the fee this ride actually collected.
  const sum = (items) => money(items.reduce((total, item) => total + item.amount, 0));
  const strict = replayFeeSources(ride, allRides, false);
  if (sum(strict) === fee) return { recorded: false, items: strict };
  const loose = replayFeeSources(ride, allRides, true);
  return { recorded: false, items: sum(loose) === fee ? loose : strict };
}

/** "2 min 17 s" between the Anna's accept and the cancel, or '' when unknown. */
export function acceptToCancelLabel(item) {
  const ms = millis(item?.cancelledAt) - millis(item?.acceptedAt);
  if (!millis(item?.acceptedAt) || !millis(item?.cancelledAt) || ms < 0) return '';
  const total = Math.round(ms / 1000);
  const min = Math.floor(total / 60);
  return min ? `${min} min ${total % 60} s` : `${total} s`;
}

/** Short one-line split for the ledger cell, e.g. "Rides ₹15 · Cancellation ₹10". Empty when nothing is owed. */
export function duesSplitLabel(owed) {
  if (!owed) return '';
  return [
    ['Rides', owed.rideFee],
    ['Night', owed.nightFee],
    ['Cancellation', owed.cancellationFee],
    ['Earlier', owed.earlier],
  ].filter(([, amount]) => amount > 0).map(([label, amount]) => `${label} ₹${amount}`).join(' · ');
}
